/**
 * Plugin Event Bus — Wave 22.13
 *
 * Host-mediated event bus for plugin-to-plugin communication.
 * Events are routed through the host (never directly between plugins)
 * and persisted in the invocation log for DevTools inspection.
 *
 * Supports:
 * - Targeted events (sourcePluginId → specific subscriber)
 * - Broadcast events ('*' wildcard)
 * - Wildcard subscriptions (listen to all plugins or all events)
 * - Subscription cleanup by plugin ID
 */

import { create } from 'zustand';
import type {
  PluginEvent,
  PluginEventHandler,
  PluginEventSubscription,
  PluginEventSubscriptionSnapshot,
} from '../types/plugin-interaction';
import { invocationLog } from './interaction-invocation-log';
import { wrapEventHandler } from './sandbox/plugin-execution-wrapper';

// ────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────

let _nextEventId = 1;
let _nextSubId = 1;

function generateEventId(): string {
  return `evt-${Date.now()}-${_nextEventId++}`;
}

function generateSubId(): string {
  return `sub-${Date.now()}-${_nextSubId++}`;
}

// ────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────

const MAX_EVENT_HISTORY = 200;

// ────────────────────────────────────────────────────────
// Store
// ────────────────────────────────────────────────────────

interface PluginEventBusState {
  /** Active subscriptions. */
  subscriptions: PluginEventSubscription[];

  /** Recent event history (newest last, capped at MAX_EVENT_HISTORY). */
  eventHistory: PluginEvent[];

  /** Emit an event from a plugin. Dispatches to matching subscribers. */
  emit(sourcePluginId: string, eventName: string, payload: any): PluginEvent;

  /**
   * Subscribe to events.
   * @param subscriberPluginId - The plugin registering the subscription.
   * @param sourcePluginId - Plugin to listen to, or '*' for any.
   * @param eventName - Event name to listen for, or '*' for any.
   * @param handler - Callback.
   * @returns Unsubscribe function.
   */
  on(
    subscriberPluginId: string,
    sourcePluginId: string,
    eventName: string,
    handler: PluginEventHandler,
  ): () => void;

  /** Remove all subscriptions owned by a plugin. */
  offAll(subscriberPluginId: string): void;

  /** DevTools-safe subscription snapshots (no handler refs). */
  getSubscriptionSnapshots(): PluginEventSubscriptionSnapshot[];

  /** Get recent events. */
  getRecentEvents(n?: number): PluginEvent[];

  /** Get events by source plugin. */
  getEventsByPlugin(pluginId: string): PluginEvent[];

  /** Clear event history. */
  clearHistory(): void;

  /** Full reset (subscriptions + history). */
  reset(): void;
}

export const usePluginEventBusStore = create<PluginEventBusState>((set, get) => ({
  subscriptions: [],
  eventHistory: [],

  emit(sourcePluginId: string, eventName: string, payload: any): PluginEvent {
    const event: PluginEvent = {
      id: generateEventId(),
      sourcePluginId,
      eventName,
      payload,
      emittedAt: new Date().toISOString(),
    };

    // Persist to history
    set((state) => {
      const newHistory = [...state.eventHistory, event];
      if (newHistory.length > MAX_EVENT_HISTORY) {
        return { eventHistory: newHistory.slice(-MAX_EVENT_HISTORY) };
      }
      return { eventHistory: newHistory };
    });

    invocationLog.append({
      kind: 'event-emit',
      pluginId: sourcePluginId,
      detail: { eventId: event.id, eventName, payload },
    });

    // Dispatch to matching subscribers
    const { subscriptions } = get();
    for (const sub of subscriptions) {
      const sourceMatches =
        sub.sourcePluginId === '*' || sub.sourcePluginId === sourcePluginId;
      const nameMatches =
        sub.eventName === '*' || sub.eventName === eventName;

      if (sourceMatches && nameMatches) {
        try {
          sub.handler(event);

          invocationLog.append({
            kind: 'event-deliver',
            pluginId: sub.subscriberPluginId,
            detail: {
              eventId: event.id,
              eventName,
              sourcePluginId,
              subscriberPluginId: sub.subscriberPluginId,
            },
          });
        } catch (err) {
          console.error(
            `[PluginEventBus] Handler error in ${sub.subscriberPluginId} for ${eventName}:`,
            err,
          );
        }
      }
    }

    return event;
  },

  on(
    subscriberPluginId: string,
    sourcePluginId: string,
    eventName: string,
    handler: PluginEventHandler,
  ): () => void {
    // Wrap handler with sandbox error boundary + slow detection
    const wrappedHandler = wrapEventHandler(subscriberPluginId, eventName, handler);

    const sub: PluginEventSubscription = {
      id: generateSubId(),
      subscriberPluginId,
      sourcePluginId,
      eventName,
      handler: wrappedHandler,
      createdAt: new Date().toISOString(),
    };

    set((state) => ({
      subscriptions: [...state.subscriptions, sub],
    }));

    // Return unsubscribe function
    return () => {
      set((state) => ({
        subscriptions: state.subscriptions.filter((s) => s.id !== sub.id),
      }));
    };
  },

  offAll(subscriberPluginId: string): void {
    set((state) => ({
      subscriptions: state.subscriptions.filter(
        (s) => s.subscriberPluginId !== subscriberPluginId,
      ),
    }));
  },

  getSubscriptionSnapshots(): PluginEventSubscriptionSnapshot[] {
    return get().subscriptions.map(({ id, subscriberPluginId, sourcePluginId, eventName, createdAt }) => ({
      id,
      subscriberPluginId,
      sourcePluginId,
      eventName,
      createdAt,
    }));
  },

  getRecentEvents(n?: number): PluginEvent[] {
    const history = get().eventHistory;
    return n ? history.slice(-n) : [...history];
  },

  getEventsByPlugin(pluginId: string): PluginEvent[] {
    return get().eventHistory.filter((e) => e.sourcePluginId === pluginId);
  },

  clearHistory(): void {
    set({ eventHistory: [] });
  },

  reset(): void {
    set({ subscriptions: [], eventHistory: [] });
  },
}));
